import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { COACHING_LIBRARY_KIND_LABELS, COACHING_LIBRARY_KINDS } from '@mlm/shared/coaching';
import { ProgramVideo } from '../components/program-video';
import { linkButton } from '../components/panels/today';
import {
  Block,
  Chip,
  CoachButton,
  ErrorNote,
  Eyebrow,
  Loading,
  Row,
  Screen,
} from '../components/primitives';
import { ProLock } from '../components/pro-lock';
import { coaching, type LibraryItem } from '../lib/api';
import { durationLabel } from '../lib/format';
import { qk, useFeatureLabel, useFeatureLocked, useTierLabels } from '../lib/queries';

type Kind = LibraryItem['kind'];

const kindLabel = (kind: Kind) => COACHING_LIBRARY_KIND_LABELS[kind];

/**
 * The content library (2026-10-13): what the team collected for members,
 * searched by the team's own words about each item. Master Kala's
 * `search_library` lands here too, at `/library/<id>`.
 */
export function LibraryPage() {
  const locked = useFeatureLocked('library');
  const plan = useFeatureLabel('library');
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<Kind | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const list = useQuery({
    queryKey: qk.library(q.trim(), kind, category),
    queryFn: () =>
      coaching.library({
        ...(q.trim() ? { q: q.trim() } : {}),
        ...(kind ? { kind } : {}),
        ...(category ? { category } : {}),
      }),
    enabled: !locked,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  if (locked) {
    return (
      <Screen back="/align" eyebrow="Library" title="Videos, audio and guides">
        <ProLock feature="library" />
      </Screen>
    );
  }
  if (list.isPending) return <Loading />;
  if (list.isError) return <ErrorNote error={list.error} retry={() => void list.refetch()} />;
  const { items, categories } = list.data;
  const categoryLabel = (key: string | null) =>
    key ? (categories.find((c) => c.key === key)?.label ?? key) : null;

  return (
    <Screen
      back="/align"
      eyebrow="Library"
      title="Videos, audio and guides"
      subtitle="Collected for you by the team. Ask Master Kala to find one by topic."
    >
      <div className="border-b border-border px-5 py-3">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the library"
          aria-label="Search the library"
          className="w-full bg-transparent py-2 text-base text-ink outline-none placeholder:text-sage"
        />
      </div>
      <div className="flex flex-wrap gap-2 border-b border-border px-5 py-4">
        <Chip selected={kind === null} onClick={() => setKind(null)}>
          All
        </Chip>
        {COACHING_LIBRARY_KINDS.map((k) => (
          <Chip key={k} selected={kind === k} onClick={() => setKind(kind === k ? null : k)}>
            {kindLabel(k)}
          </Chip>
        ))}
      </div>
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 border-b border-border px-5 py-4">
          {categories.map((c) => (
            <Chip
              key={c.key}
              selected={category === c.key}
              onClick={() => setCategory(category === c.key ? null : c.key)}
            >
              {c.label}
            </Chip>
          ))}
        </div>
      )}
      {items.length === 0 ? (
        <Block title={q.trim() ? 'Nothing matches' : 'Nothing here yet'}>
          {q.trim()
            ? 'Try another word, or ask Master Kala what the library has on it.'
            : 'Items appear here as the team adds them.'}
        </Block>
      ) : (
        items.map((item) => (
          <Row
            key={item.id}
            title={item.title}
            meta={[
              kindLabel(item.kind),
              durationLabel(item.durationSeconds),
              categoryLabel(item.category),
              item.locked ? plan : null,
            ]
              .filter(Boolean)
              .join(' · ')}
            to={`/library/${item.id}`}
          />
        ))
      )}
    </Screen>
  );
}

export function LibraryItemPage() {
  const { id = '' } = useParams();
  const locked = useFeatureLocked('library');
  const labels = useTierLabels();
  const found = useQuery({
    queryKey: qk.libraryItem(id),
    queryFn: () => coaching.libraryItem(id),
    enabled: !locked && Boolean(id),
    // A presigned URL lives an hour; refetch before it lapses.
    staleTime: 50 * 60 * 1000,
  });

  if (locked) {
    return (
      <Screen back="/library" eyebrow="Library" title="Videos, audio and guides">
        <ProLock feature="library" />
      </Screen>
    );
  }
  if (found.isPending) return <Loading />;
  if (found.isError) {
    return (
      <Screen back="/library" eyebrow="Library" title="This item is not available">
        <Block
          title="It may have been removed"
          action={
            <Link to="/library" className={linkButton}>
              Back to the library
            </Link>
          }
        >
          Choose another item from the library.
        </Block>
      </Screen>
    );
  }
  const { item, open } = found.data;
  const planName = labels[item.premiumLevel];

  return (
    <Screen back="/library" eyebrow={`Library · ${kindLabel(item.kind)}`} title={item.title}>
      {item.locked ? (
        <section className="border-b border-border px-5 py-6">
          <Eyebrow className="mb-2">{planName}</Eyebrow>
          <p className="text-[0.95rem] leading-relaxed text-ink/70">
            This {kindLabel(item.kind).toLowerCase()} is part of {planName}.
          </p>
          <Link to="/upgrade" className="mt-5 inline-block">
            <CoachButton kind="gold">See what {planName} adds</CoachButton>
          </Link>
        </section>
      ) : (
        <ItemMedia item={item} openUrl={open?.url ?? null} />
      )}
      {item.description && (
        <p className="px-5 py-5 text-[0.95rem] leading-relaxed text-ink/80 lg:max-w-prose">
          {item.description}
        </p>
      )}
      {item.durationSeconds && (
        <p className="eyebrow px-5 pb-6">{durationLabel(item.durationSeconds)}</p>
      )}
    </Screen>
  );
}

/** The item itself: a player, a picture, or a button that opens the file. */
function ItemMedia({ item, openUrl }: { item: LibraryItem; openUrl: string | null }) {
  const embed = item.embed;
  if (item.kind === 'video' || (item.kind === 'audio' && embed?.kind === 'iframe')) {
    if (!embed) return null;
    return <ProgramVideo src={embed.src} title={item.title} />;
  }
  if (item.kind === 'audio') {
    const src = openUrl ?? (embed?.kind === 'video' ? embed.src : null);
    if (!src) return null;
    return (
      <div className="border-b border-border px-5 py-5">
        <audio controls preload="metadata" src={src} className="w-full" />
      </div>
    );
  }
  if (item.kind === 'image') {
    const src = openUrl ?? embed?.src ?? null;
    if (!src) return null;
    return (
      <div className="border-b border-border">
        <img src={src} alt={item.title} className="block w-full" />
      </div>
    );
  }
  const href = openUrl ?? embed?.src ?? null;
  if (!href) return null;
  return (
    <div className="border-b border-border px-5 py-5">
      <a href={href} target="_blank" rel="noopener" className="inline-block">
        <CoachButton kind="gold">Open the document</CoachButton>
      </a>
      <p className="eyebrow mt-3">Opens in your browser.</p>
    </div>
  );
}
